import React, { useContext, useEffect, useState } from "react";
import { Container, Row, Col, Tab, Nav } from "react-bootstrap";
import "../../assets/css/donordashboard/RewardsAndBadges.css";
import MasterLayout from "../../components/layout/MasterLayout";
import AppContext from "../../context/AppContext";

// ─── localStorage helpers ──────────────────────────────────────────────────
const getClaimedBadges = (donorId) => {
  try {
    const raw = localStorage.getItem(`claimed_badges_${donorId}`);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
};

const saveClaimedBadge = (donorId, badgeId) => {
  const existing = getClaimedBadges(donorId);
  if (!existing.includes(badgeId)) {
    localStorage.setItem(`claimed_badges_${donorId}`, JSON.stringify([...existing, badgeId]));
  }
};

// ─── Leaderboard static data ──────────────────────────────────────────────
const LEADERBOARD = [
  { rank: 1, name: "Ali Hassan",  donations: 42, avatar: "A", color: "#c8960c" },
  { rank: 2, name: "Sara Ahmed",  donations: 35, avatar: "S", color: "#2D9B8E" },
  { rank: 3, name: "Ahmed Khan",  donations: 28, avatar: "K", color: "#1F7A7A" },
];

// ─── Progress Ring ────────────────────────────────────────────────────────
function PointsProgressRing({ current, target, size = 80 }) {
  const radius = (size - 10) / 2;
  const circum = 2 * Math.PI * radius;
  const pct    = Math.min(current / target, 1);
  const offset = circum * (1 - pct);
  const done   = pct >= 1;

  return (
    <div className="rb-ring-wrap" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={radius}
          fill="none" stroke={done ? "rgba(255,255,255,0.2)" : "#e9ecef"} strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={radius}
          fill="none"
          stroke={done ? "#ffd700" : "#1F7A7A"}
          strokeWidth={8}
          strokeDasharray={circum}
          strokeDashoffset={offset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
      </svg>
      <div className="rb-ring-label" style={{ color: done ? "#ffd700" : "#1F7A7A" }}>
        {done ? "✓" : `${Math.round(pct * 100)}%`}
      </div>
    </div>
  );
}

// ─── Badge Card ───────────────────────────────────────────────────────────
function BadgeCard({ badge, onClaim }) {
  const { earned, claimed, name, description, progress, target, points, rewardValue } = badge;
  const pct = Math.min(Math.round((progress / target) * 100), 100);

  return (
    <div className={`rb-badge-card ${earned ? (claimed ? "rb-badge-claimed" : "rb-badge-earned") : "rb-badge-locked"}`}>
      {earned && !claimed && <div className="rb-earned-ribbon">✓ EARNED</div>}
      {claimed && <div className="rb-earned-ribbon" style={{ background: "#198754" }}>✓ CLAIMED</div>}

      <div className="rb-badge-inner">
        <div className="rb-icon-wrap">
          <PointsProgressRing current={progress} target={target} size={86} />
          <div className="rb-badge-icon">{earned ? "🏅" : "🎖️"}</div>
        </div>

        <h5 className={`rb-badge-name ${earned ? "rb-text-white" : ""}`}>{name}</h5>
        <p className={`rb-badge-desc ${earned ? "rb-text-muted-white" : ""}`}>{description}</p>

        {/* ── Custom Progress Bar ── */}
        <div className="rb-progress-wrap">
          <div className="rb-progress-label">
            <span className={earned ? "rb-text-muted-white" : "rb-progress-label-dark"}>
              {earned ? "Completed! 🎉" : `${progress}/${target} donations`}
            </span>
          </div>
          <div className={`rb-progress-track ${earned ? "rb-progress-track-earned" : "rb-progress-track-locked"}`}>
            <div
              className={`rb-progress-fill ${earned ? "rb-progress-fill-earned" : "rb-progress-fill-locked"}`}
              style={{ width: `${earned ? 100 : pct}%` }}
            />
          </div>
        </div>

        {/* Reward amount */}
        {rewardValue > 0 && (
          <div className="rb-reward-box" style={{
            background: earned ? "rgba(255,255,255,0.15)" : "#f8f9fa",
            border: earned ? "1px solid rgba(255,255,255,0.3)" : "1px solid #dee2e6",
          }}>
            <div style={{ fontSize: "0.75rem", color: earned ? "rgba(255,255,255,0.7)" : "#888" }}>
              Reward Amount
            </div>
            <div style={{ fontSize: "1.1rem", fontWeight: 700, color: earned ? "#ffd700" : "#1F7A7A" }}>
              Rs. {rewardValue.toLocaleString()}
            </div>
          </div>
        )}

        {/* Points tag */}
        <div className={`rb-points-tag ${earned ? "rb-points-earned" : "rb-points-locked"}`}>
          {earned ? `🏆 ${points} pts earned!` : `${points} pts to unlock`}
        </div>

        {/* Claim button */}
        {earned && !claimed && rewardValue > 0 && (
          <button className="rb-claim-btn-gold" onClick={() => onClaim(badge)}>
            💰 Withdraw Rs. {rewardValue.toLocaleString()}
          </button>
        )}

        {claimed && (
          <div className="rb-claimed-msg">
            ✅ Amount withdrawal mein add ho gaya
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────
function RewardsAndBadges() {
  const { loggedInDonorId, baseUrl } = useContext(AppContext);

  const [activeTab,      setActiveTab]      = useState("badges");
  const [badges,         setBadges]         = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [donorDonations, setDonorDonations] = useState(0);
  const [donorName,      setDonorName]      = useState("");
  const [balance,        setBalance]        = useState(() => {
    try {
      const id  = JSON.parse(localStorage.getItem("loggedInDonor") || "{}").id;
      const raw = localStorage.getItem(`rb_balance_${id}`);
      return raw ? Number(raw) : 0;
    } catch { return 0; }
  });
  const [toast, setToast] = useState(null);

  const effectiveDonorId = loggedInDonorId || (() => {
    try { return JSON.parse(localStorage.getItem("loggedInDonor") || "{}").id || null; }
    catch { return null; }
  })();

  useEffect(() => {
    if (!effectiveDonorId) return;
    fetch(`${baseUrl}/donor/dashboard/viewProfile/${effectiveDonorId}`)
      .then((r) => r.json())
      .then((res) => {
        const d = res?.profile || res?.donor || res;
        setDonorName(d?.name || d?.fullname || d?.username || "");
      })
      .catch(() => {
        try {
          const s = JSON.parse(localStorage.getItem("loggedInDonor") || "{}");
          setDonorName(s?.name || s?.fullname || "");
        } catch {}
      });
  }, [effectiveDonorId]);

  useEffect(() => {
    if (!effectiveDonorId) return;
    const id = String(effectiveDonorId);

    Promise.allSettled([
      fetch(`${baseUrl}/donor/dashboard/viewAllBloodDonations?donor_id=${id}`).then(r => r.json()),
      fetch(`${baseUrl}/receiver/blood/viewAllRequests?donor_id=${id}`).then(r => r.json()),
      fetch(`${baseUrl}/donor/dashboard/viewAllDonation?donor_id=${id}`).then(r => r.json()),
      fetch(`${baseUrl}/donor/dashboard/viewAllMedicine?donor_id=${id}`).then(r => r.json()),
    ]).then(([bloodRes, receiverRes, foodRes, medicineRes]) => {
      let total = 0;

      if (bloodRes.status === "fulfilled") {
        const list = bloodRes.value?.donation || bloodRes.value?.data || bloodRes.value || [];
        if (Array.isArray(list))
          total += list.filter(d => String(d.donor_id) === id || String(d.id) === id).length;
      }
      if (receiverRes.status === "fulfilled") {
        const list = receiverRes.value?.requests || receiverRes.value?.data || receiverRes.value || [];
        if (Array.isArray(list))
          total += list.filter(r => String(r.donor_id) === id && (r.status||"").toLowerCase() === "approved").length;
      }
      if (foodRes.status === "fulfilled") {
        const list = foodRes.value?.donation || foodRes.value?.data || foodRes.value || [];
        if (Array.isArray(list)) total += list.length;
      }
      if (medicineRes.status === "fulfilled") {
        const list = medicineRes.value?.donations || medicineRes.value?.data || medicineRes.value || [];
        if (Array.isArray(list)) total += list.length;
      }

      setDonorDonations(total);
      fetchBadges(total);
    });
  }, [effectiveDonorId]);

  const fetchBadges = async (currentDonations) => {
    try {
      setLoading(true);
      const res  = await fetch(`${baseUrl}/badge-levels`);
      const data = await res.json();
      const claimed = getClaimedBadges(effectiveDonorId);

      const badgeData = data
        .filter(item => item.type === "Badge" || !item.type)
        .map(item => {
          const triggerNum = Number(item.trigger);
          const isEarned   = Number(currentDonations) >= triggerNum;
          return {
            id:          item.id,
            name:        item.title,
            description: `${triggerNum} donations required`,
            earned:      isEarned,
            claimed:     claimed.includes(item.id),
            progress:    Math.min(Number(currentDonations), triggerNum),
            target:      triggerNum,
            points:      triggerNum * 10,
            rewardValue: Number(item.rewardValue) || Number(item.reward_value) || 0,
          };
        });

      setBadges(badgeData);
    } catch (err) {
      console.error("fetchBadges error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimBadge = (badge) => {
    if (!badge.earned || badge.claimed) return;
    const newBalance = balance + badge.rewardValue;
    setBalance(newBalance);
    try { localStorage.setItem(`rb_balance_${effectiveDonorId}`, String(newBalance)); } catch {}
    saveClaimedBadge(effectiveDonorId, badge.id);
    try {
      const existing = JSON.parse(localStorage.getItem(`rb_pending_rewards_${effectiveDonorId}`) || "[]");
      existing.push({ badgeId: badge.id, name: badge.name, amount: badge.rewardValue, claimedAt: new Date().toISOString() });
      localStorage.setItem(`rb_pending_rewards_${effectiveDonorId}`, JSON.stringify(existing));
    } catch {}
    setBadges(prev => prev.map(b => b.id === badge.id ? { ...b, claimed: true } : b));
    showToast(`🎉 Rs. ${badge.rewardValue.toLocaleString()} withdrawal mein add ho gaya!`);
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  };

  const totalPoints   = badges.filter(b => b.earned).reduce((s, b) => s + b.points, 0);
  const earnedCount   = badges.filter(b => b.earned).length;
  const donationsLeft = (() => {
    const unearned = badges.filter(b => !b.earned);
    if (!unearned.length) return 0;
    return Math.min(...unearned.map(b => b.target)) - donorDonations;
  })();

  return (
    <div className="rb-page">
      <MasterLayout>

        {/* HERO */}
        <div className="rb-hero">
          <div className="rb-hero-bg" />
          <Container className="rb-hero-content">
            <div className="rb-hero-icon">🏆</div>
            <h1 className="rb-hero-title">Rewards & Badges</h1>
            <p className="rb-hero-sub">Apni achievements track karo aur rewards claim karo</p>
            {donorName && <div className="rb-hero-name">Welcome, {donorName}! 👋</div>}
          </Container>
        </div>

        <Container className="rb-container">

          {/* BALANCE CARD */}
          <Row className="mb-4">
            <Col>
              <div className="rb-balance-card">
                <div className="rb-balance-left">
                  <div className="rb-balance-label">Withdrawal Balance</div>
                  <div className="rb-balance-amount">Rs. {balance.toLocaleString()}</div>
                  <div className="rb-balance-hint">
                    Badge earn karo → Withdraw karo → Payout page se withdraw request karo
                  </div>
                </div>
                <div className="rb-balance-icon">💰</div>
              </div>
            </Col>
          </Row>

          {/* STATS */}
          <Row className="mb-4">
            {[
              { icon: "🎯", value: donorDonations,                    label: "Total Donations" },
              { icon: "🏅", value: `${earnedCount}/${badges.length}`, label: "Badges Earned" },
              { icon: "⭐", value: totalPoints,                        label: "Points Earned" },
              { icon: "💰", value: `Rs. ${balance.toLocaleString()}`,  label: "Withdrawal Balance" },
            ].map((s, i) => (
              <Col key={i} lg={3} md={6} className="mb-3">
                <div className="rb-stat-card">
                  <div className="rb-stat-icon">{s.icon}</div>
                  <div className="rb-stat-value">{s.value}</div>
                  <div className="rb-stat-label">{s.label}</div>
                </div>
              </Col>
            ))}
          </Row>

          {/* HINT */}
          {donationsLeft > 0 && (
            <div className="rb-hint-bar">
              <span>🔥</span>
              <span>Agle badge ke liye sirf <strong>{donationsLeft} more donations</strong> chahiye!</span>
            </div>
          )}

          {/* TABS */}
          <div className="rb-tabs-card">
            <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
              <Nav className="rb-nav">
                {[
                  { key: "badges",      label: "🎖️ My Badges" },
                  { key: "leaderboard", label: "🏆 Leaderboard" },
                ].map(({ key, label }) => (
                  <Nav.Item key={key}>
                    <Nav.Link eventKey={key} className={`rb-nav-link ${activeTab === key ? "rb-nav-active" : ""}`}>
                      {label}
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>

              <Tab.Content className="rb-tab-content">

                {/* BADGES TAB */}
                <Tab.Pane eventKey="badges">
                  <h4 className="rb-section-title">Your Achievement Badges</h4>
                  {loading ? (
                    <div className="rb-loading">
                      <div className="rb-spinner" />
                      <p>Loading badges...</p>
                    </div>
                  ) : badges.length === 0 ? (
                    <div className="rb-empty">
                      <div style={{ fontSize: 48 }}>🎖️</div>
                      <p>NGO ne abhi koi badge nahi banaya.</p>
                    </div>
                  ) : (
                    <Row>
                      {badges.map(badge => (
                        <Col key={badge.id} lg={3} md={4} sm={6} className="mb-4">
                          <BadgeCard badge={badge} onClaim={handleClaimBadge} />
                        </Col>
                      ))}
                    </Row>
                  )}
                </Tab.Pane>

                {/* LEADERBOARD TAB */}
                <Tab.Pane eventKey="leaderboard">
                  <h4 className="rb-section-title">Top Donors</h4>
                  <div className="rb-leaderboard">
                    {LEADERBOARD.map(user => (
                      <div key={user.rank} className="rb-lb-row">
                        <div className={`rb-lb-rank rank-${user.rank}`}>{user.rank}</div>
                        <div className="rb-lb-avatar" style={{ background: user.color }}>{user.avatar}</div>
                        <div className="rb-lb-info">
                          <div className="rb-lb-name">{user.name}</div>
                          <div className="rb-lb-donations">{user.donations} donations</div>
                        </div>
                        <div className="rb-lb-pts">
                          <div className="rb-lb-pts-val">{user.donations * 10}</div>
                          <div className="rb-lb-pts-lbl">points</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Tab.Pane>

              </Tab.Content>
            </Tab.Container>
          </div>

        </Container>

        {toast && <div className="rb-toast">{toast}</div>}

      </MasterLayout>
    </div>
  );
}

export default RewardsAndBadges;